import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Search } from "lucide-react";
import { useState } from "react";

import { fetchCodex } from "@/lib/api";
import { getCodexRuleUrl, navigateReviewLink } from "@/lib/review-navigation";
import { useStore } from "@/lib/store";

import { PageViewHeader } from "../PageViewHeader";
import { ReviewSectionTabs } from "./ReviewSectionTabs";

export function CodexView(): React.JSX.Element {
  const [search, setSearch] = useState("");
  const setView = useStore((state) => state.setView);
  const { data, isLoading, error } = useQuery({
    queryKey: ["codex"],
    queryFn: fetchCodex,
  });
  const normalizedSearch = search.trim().toLowerCase();
  const rules =
    data?.rules.filter(
      (rule) =>
        !normalizedSearch ||
        rule.id.toLowerCase().includes(normalizedSearch) ||
        rule.domain.toLowerCase().includes(normalizedSearch) ||
        rule.rule.toLowerCase().includes(normalizedSearch),
    ) ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageViewHeader
        title="PR Reviews"
        badge={
          data ? (
            <span className="bg-cyber-surface text-cyber-muted rounded-full px-1.5 py-0.5 text-[10px]">
              {data.rules.length} rules
            </span>
          ) : null
        }
        actions={<ReviewSectionTabs active="codex" />}
      />

      <main className="flex-1 overflow-y-auto px-4 py-8 md:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="border-cyber-border flex flex-col gap-5 border-b pb-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-cyber-cyan text-[10px] tracking-[0.24em] uppercase">
                Engineering standards
              </div>
              <h1 className="text-cyber-text mt-2 text-2xl font-semibold md:text-3xl">
                PR Review Codex
              </h1>
              <p className="text-cyber-muted mt-2 max-w-2xl text-sm leading-6">
                Approved Bold standards that can be cited in Sherlog PR review findings.
              </p>
            </div>
            {data ? (
              <div className="text-cyber-muted font-mono text-xs">Version {data.version}</div>
            ) : null}
          </div>

          <div className="relative my-6">
            <Search
              size={14}
              className="text-cyber-muted pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by ID, domain, or rule"
              aria-label="Search Codex rules"
              className="border-cyber-border bg-cyber-surface/30 text-cyber-text placeholder:text-cyber-muted focus:border-cyber-cyan w-full rounded border py-2.5 pr-3 pl-9 text-sm transition-colors outline-none"
            />
          </div>

          {isLoading ? (
            <div className="text-cyber-muted py-20 text-center text-sm">Loading rules...</div>
          ) : error ? (
            <div className="rounded border border-red-500/20 bg-red-500/5 p-5 text-sm text-red-400">
              The Codex could not be loaded.
            </div>
          ) : rules.length === 0 ? (
            <div className="border-cyber-border bg-cyber-surface/20 text-cyber-muted rounded border p-8 text-center text-sm">
              No rules match this search.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {rules.map((rule) => (
                <a
                  key={rule.id}
                  href={getCodexRuleUrl(rule.id)}
                  onClick={(event) =>
                    navigateReviewLink(
                      event,
                      getCodexRuleUrl(rule.id),
                      "github-reviews-codex-rule",
                      setView,
                    )
                  }
                  className="group border-cyber-border bg-cyber-surface/20 hover:border-cyber-cyan/60 hover:bg-cyber-surface/40 flex min-h-40 flex-col rounded border p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-cyber-text font-mono text-sm font-semibold">
                        {rule.id}
                      </span>
                      <span className="bg-cyber-cyan/10 text-cyber-cyan rounded px-1.5 py-0.5 text-[10px] font-medium">
                        {rule.level}
                      </span>
                    </div>
                    <ChevronRight
                      size={15}
                      className="text-cyber-muted group-hover:text-cyber-cyan shrink-0 transition-transform group-hover:translate-x-0.5"
                    />
                  </div>
                  <div className="text-cyber-muted mt-2 text-[10px] tracking-wider uppercase">
                    {rule.domain}
                  </div>
                  <p className="text-cyber-text mt-3 text-sm leading-6">{rule.rule}</p>
                  <div className="text-cyber-muted mt-auto pt-4 text-[10px] tracking-wider uppercase">
                    {rule.status} / revision {rule.revision}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
